import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle, ChatsCircle, MagnifyingGlass, PaperPlaneTilt, User, UsersThree, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { messagesApi, toAbsolute } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const PassportShareContext = createContext({ openPassportShare: () => {} });

export function PassportShareProvider({ children }) {
    const { user } = useAuth();
    const [state, setState] = useState({ open: false, shareRef: null, sourceLabel: null });

    const openPassportShare = useCallback((payload) => {
        if (!user) {
            toast.error("Sign in to share in Passport Messages.");
            return;
        }
        setState({ open: true, shareRef: payload?.shareRef || null, sourceLabel: payload?.sourceLabel || null });
    }, [user]);

    const closePassportShare = useCallback(() => setState((prev) => ({ ...prev, open: false })), []);

    return (
        <PassportShareContext.Provider value={{ openPassportShare }}>
            {children}
            <PassportShareModal open={state.open} shareRef={state.shareRef} sourceLabel={state.sourceLabel} onClose={closePassportShare} />
        </PassportShareContext.Provider>
    );
}

export function usePassportShare() {
    return useContext(PassportShareContext);
}

// ─── Instagram-style 1-click share modal ─────────────────────────────────────

function PassportShareModal({ open, shareRef, sourceLabel, onClose }) {
    const [preview, setPreview] = useState(null);
    const [recents, setRecents] = useState([]);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(new Set()); // keys: "conv:{id}" or "user:{handle}"
    const [selectedMeta, setSelectedMeta] = useState({}); // key → {label, type, conversationId?, handle?}
    const [caption, setCaption] = useState("");
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingRecents, setLoadingRecents] = useState(false);
    const [sentTo, setSentTo] = useState(null); // {count} when done
    const [sending, setSending] = useState(null); // key being sent or null

    useEffect(() => {
        if (!open || !shareRef) return;
        setPreview(null); setRecents([]); setQuery(""); setResults([]);
        setSelected(new Set()); setSelectedMeta({}); setCaption(""); setSentTo(null); setSending(null);
        setLoadingPreview(true); setLoadingRecents(true);
        messagesApi.sharedPreview(shareRef)
            .then(setPreview)
            .catch((error) => { toast.error(error?.response?.data?.detail || "This Passport item is unavailable to share."); onClose?.(); })
            .finally(() => setLoadingPreview(false));
        messagesApi.conversations({ limit: 12 })
            .then((data) => setRecents((data.items || []).slice(0, 10)))
            .catch(() => setRecents([]))
            .finally(() => setLoadingRecents(false));
    }, [open, shareRef, onClose]);

    useEffect(() => {
        if (!open || query.trim().length < 1) { setResults([]); return undefined; }
        const timer = setTimeout(() => {
            messagesApi.shareRecipients(query.trim()).then(setResults).catch(() => setResults([]));
        }, 180);
        return () => clearTimeout(timer);
    }, [open, query]);

    // Toggle selection of a conversation from recents
    const toggleConv = (conv) => {
        const key = `conv:${conv.id}`;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
        setSelectedMeta((prev) => {
            if (prev[key]) { const { [key]: _, ...rest } = prev; return rest; }
            return { ...prev, [key]: { label: conv.title || conv.other_user_name || "Passport chat", type: "conv", conversationId: conv.id, isGroup: conv.type === "group" } };
        });
    };

    // 1-click instant send to a single recipient or conversation
    const instantSend = async (target) => {
        if (!shareRef || sending) return;
        const key = target.key;
        setSending(key);
        try {
            const payload = {
                text: caption.trim() || null,
                share_ref: shareRef,
                conversation_ids: target.conversationId ? [target.conversationId] : [],
                recipient_handles: target.handle ? [target.handle] : [],
            };
            await messagesApi.sharePassport(payload);
            toast.success(`Sent to ${target.label}`, { duration: 2500 });
            setSentTo((prev) => ({ count: (prev?.count || 0) + 1 }));
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not send.");
        } finally {
            setSending(null);
        }
    };

    // Send to all selected recents at once
    const sendToSelected = async (event) => {
        event?.preventDefault?.();
        if (!shareRef || selected.size === 0 || sending) return;
        const convIds = [...selected].filter((k) => k.startsWith("conv:")).map((k) => k.replace("conv:", ""));
        const handles = [...selected].filter((k) => k.startsWith("user:")).map((k) => k.replace("user:", ""));
        setSending("batch");
        try {
            const res = await messagesApi.sharePassport({ text: caption.trim() || null, share_ref: shareRef, conversation_ids: convIds, recipient_handles: handles });
            toast.success(`Shared to ${res.summary?.sent_count || selected.size} recipient(s).`);
            setSentTo({ count: selected.size });
            setSelected(new Set()); setSelectedMeta({});
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not share.");
        } finally {
            setSending(null);
        }
    };

    const selectedCount = selected.size;
    const displayList = query.trim().length > 0 ? results : recents;
    const isSearchMode = query.trim().length > 0;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center p-0 sm:p-4"
                    style={{ background: "rgba(5,10,17,0.88)", backdropFilter: "blur(12px)" }}
                    onClick={onClose}
                    data-testid="passport-share-modal"
                >
                    <motion.div
                        initial={{ y: 48, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 32, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 320, damping: 32 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:max-w-sm rounded-t-[32px] sm:rounded-[32px] flex flex-col overflow-hidden"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.22)", maxHeight: "90vh" }}
                    >
                        {/* ── Header ── */}
                        <div className="px-5 pt-5 pb-3 shrink-0">
                            <div className="flex items-center justify-between mb-4">
                                <div className="text-sm font-semibold text-white">Send in Passport</div>
                                <button type="button" onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.07)" }} data-testid="passport-share-close"><X size={14} /></button>
                            </div>

                            {/* Preview card (compact) */}
                            {preview && (
                                <div className="flex items-center gap-3 rounded-2xl px-3 py-2.5 mb-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }} data-testid="passport-share-preview-card">
                                    {preview.image_url && !preview.image_url.match(/\.(mp4|webm|mov|mkv)(\?.*)?$/i) ? (
                                        <>
                                            <img 
                                                src={toAbsolute(preview.image_url)} 
                                                alt={preview.title} 
                                                className="w-10 h-10 rounded-xl object-cover shrink-0" 
                                                data-testid="passport-share-preview-image" 
                                                onError={(e) => {
                                                    e.currentTarget.style.display = "none";
                                                    if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = "grid";
                                                }}
                                            />
                                            <div className="w-10 h-10 rounded-xl place-items-center shrink-0 text-white/40" style={{ display: "none", background: "rgba(255,255,255,0.05)" }}>
                                                <ChatsCircle size={18} />
                                            </div>
                                        </>
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 text-white/40" style={{ background: "rgba(255,255,255,0.05)" }}>
                                            <ChatsCircle size={18} />
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-0.5">{preview.entity_type}</div>
                                        <div className="text-sm text-white truncate" data-testid="passport-share-preview-title">{preview.title}</div>
                                        {preview.subtitle && <div className="text-xs text-white/50 truncate" data-testid="passport-share-preview-subtitle">{preview.subtitle}</div>}
                                    </div>
                                </div>
                            )}
                            {loadingPreview && (
                                <div className="rounded-2xl px-3 py-4 text-sm text-white/45 text-center" style={{ background: "rgba(255,255,255,0.03)" }} data-testid="passport-share-preview-loading">Loading preview…</div>
                            )}

                            {/* Optional caption */}
                            <input
                                value={caption} onChange={(e) => setCaption(e.target.value)}
                                placeholder="Add a caption (optional)"
                                className="w-full rounded-2xl px-3 py-2.5 bg-black/25 text-white border text-sm outline-none mb-3"
                                style={{ borderColor: "rgba(255,255,255,0.08)" }}
                                data-testid="passport-share-caption-input"
                            />

                            {/* Search */}
                            <div className="relative">
                                <MagnifyingGlass size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    value={query} onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search travelers or chats"
                                    className="w-full rounded-2xl pl-9 pr-4 py-2.5 bg-black/25 text-white border text-sm outline-none"
                                    style={{ borderColor: "rgba(255,255,255,0.08)" }}
                                    data-testid="passport-share-search-input"
                                />
                            </div>
                        </div>

                        {/* ── Recipient list ── */}
                        <div className="flex-1 overflow-y-auto px-3 pb-2" data-testid="passport-share-recents-list">
                            {loadingRecents && !isSearchMode && (
                                <div className="text-sm text-white/45 py-8 text-center" data-testid="passport-share-recents-loading">Loading chats…</div>
                            )}
                            {!loadingRecents && displayList.length === 0 && (
                                <div className="text-sm text-white/45 py-8 text-center" data-testid="passport-share-recents-empty">
                                    {isSearchMode ? "No matches found." : "No recent chats yet."}
                                </div>
                            )}
                            {displayList.map((item) => {
                                // item is either a conversation (from recents) or a recipient (from search)
                                const isConv = "last_message_preview" in item || "other_user_name" in item;
                                const key = isConv ? `conv:${item.id}` : `user:${item.handle}`;
                                const isPicked = selected.has(key);
                                const label = isConv ? (item.title || item.other_user_name || "Passport chat") : item.name;
                                const sub = isConv ? (item.last_message_preview || (item.type === "group" ? "Group" : `@${item.other_user_handle}`)) : `@${item.handle}`;
                                const avatar = isConv ? item.other_user_avatar : item.avatar_url;
                                const isGroup = isConv && item.type === "group";
                                // If the search result already has an established conversation,
                                // route it as a conversation-id send (avoids spurious requests).
                                const sendTarget = isConv
                                    ? { key, label, conversationId: item.id }
                                    : item.conversation_id
                                        ? { key, label, conversationId: item.conversation_id }
                                        : { key, label, handle: item.handle };
                                const isSending = sending === key;

                                return (
                                    <div
                                        key={key}
                                        className="flex items-center gap-3 px-2 py-2.5 rounded-2xl transition-colors"
                                        style={{ background: isPicked ? "rgba(212,175,55,0.08)" : "transparent" }}
                                        data-testid={isConv ? `passport-share-recent-${item.id}` : `passport-share-result-${item.handle}`}
                                    >
                                        {/* Avatar */}
                                        <div className="w-11 h-11 rounded-full overflow-hidden shrink-0" style={{ background: "rgba(255,255,255,0.06)" }}>
                                            {isGroup
                                                ? <div className="w-full h-full grid place-items-center text-white/60"><UsersThree size={18} /></div>
                                                : avatar
                                                    ? <img src={avatar} alt="" className="w-full h-full object-cover" />
                                                    : <div className="w-full h-full grid place-items-center text-white/60"><User size={18} /></div>
                                            }
                                        </div>

                                        {/* Name / sub */}
                                        <div className="flex-1 min-w-0" onClick={() => isConv ? toggleConv(item) : null}>
                                            <div className="text-sm text-white truncate font-medium">{label}</div>
                                            <div className="text-xs text-white/45 truncate">{sub}</div>
                                        </div>

                                        {/* 1-click Send OR selection checkbox */}
                                        {isConv ? (
                                            /* For recent conversations: checkbox + quick-send */
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleConv(item)}
                                                    className="w-6 h-6 rounded-full grid place-items-center border transition-colors"
                                                    style={{
                                                        background: isPicked ? "var(--gold)" : "transparent",
                                                        borderColor: isPicked ? "var(--gold)" : "rgba(255,255,255,0.2)",
                                                    }}
                                                    data-testid={`passport-share-select-${item.id}`}
                                                >
                                                    {isPicked && <CheckCircle size={14} weight="fill" color="var(--navy)" />}
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={!!sending}
                                                    onClick={() => instantSend(sendTarget)}
                                                    className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all disabled:opacity-50"
                                                    style={{ background: isSending ? "rgba(212,175,55,0.4)" : "var(--gold)", color: "var(--navy)" }}
                                                    data-testid={`passport-share-send-${item.id}`}
                                                >
                                                    {isSending ? "…" : "Send"}
                                                </button>
                                            </div>
                                        ) : (
                                            /* For search results: direct send */
                                            <button
                                                type="button"
                                                disabled={!!sending}
                                                onClick={() => instantSend(sendTarget)}
                                                className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all disabled:opacity-50 shrink-0"
                                                style={{ background: isSending ? "rgba(212,175,55,0.4)" : "var(--gold)", color: "var(--navy)" }}
                                                data-testid={`passport-share-result-send-${item.handle}`}
                                            >
                                                {isSending ? "…" : item.delivery_mode === "request" ? "Request" : "Send"}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* ── Footer: send to selected or close ── */}
                        <div className="px-5 py-4 border-t shrink-0" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            {sentTo ? (
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 text-sm" style={{ color: "var(--gold)" }}>
                                        <CheckCircle size={16} weight="fill" /> Shared to {sentTo.count} recipient{sentTo.count !== 1 ? "s" : ""}
                                    </div>
                                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ background: "var(--gold)", color: "var(--navy)" }} data-testid="passport-share-result-done">Done</button>
                                </div>
                            ) : selectedCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={sendToSelected}
                                    disabled={sending === "batch"}
                                    className="w-full py-3 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                                    style={{ background: "var(--gold)", color: "var(--navy)" }}
                                    data-testid="passport-share-submit"
                                >
                                    <PaperPlaneTilt size={15} weight="fill" />
                                    {sending === "batch" ? "Sending…" : `Send to ${selectedCount} selected`}
                                </button>
                            ) : (
                                <button type="button" onClick={onClose} className="w-full py-3 rounded-2xl text-sm text-white/50 transition-all" style={{ background: "rgba(255,255,255,0.04)" }} data-testid="passport-share-cancel">Cancel</button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}